import React, {Fragment} from 'react';

import {Box, Stack, Typography} from '@mui/material';
import {Drawer} from 'presentation/components/Drawer';
import {Filter} from 'presentation/components/Filter';
import {PlanTypeEnum} from 'presentation/constants';
import {CustomSlider} from 'presentation/modules/booking/shared/ui/components/DistanceSlider/styles';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import {textHandle} from 'presentation/utils/textHandle';
import {CurrentLocation} from '../CurrentLocation';
import {AutocompleteSelect} from '../SearchDetails/AutocompleteSelect';
import {SkeletonInput} from '../SkeletonInput';
import {SortButtons} from '../SortButtons';
import {useSearchBarUnit} from './hooks/useSearchBarUnit';
import {SearchBarUnitProps} from './types';

const {searchUnit} = translations['pt-br'].commons;
const {selectDistrict} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow;
const {
	selectSpecificNeighborhood,
	specificNeighborhood,
	loadingDistricts,
	filter,
	filterDistance,
	distance: distanceText,
	selectDistance,
	selectDistanceCoordinates,
	fromYourLocation,
	maxDistance,
} = translations['pt-br'].privateFlow.guideFlow;

export function SearchBarUnit({type = PlanTypeEnum.HEALTH, providers}: SearchBarUnitProps) {
	const {
		setSearchByProviderName,
		providerName,
		handleOpenDrawer,
		openDrawerState,
		districtList,
		districtListIsLoading,
		filterDistrictsByProviders,
		formatDistrictValue,
		localSelectDistrict,
		handleDistrictSelection,
		localSortOrder,
		setLocalSortOrder,
		handleApplyFilter,
		handleClearFilter,
		isDistanceFeatureEnabled,
		isHealthGuide,
		distanceSliderValue,
		handleChangeDistance,
		DISTANCE_MARKS,
		activeFiltersCount,
		providerGuideTagging,
		distanceIndex,
		healthCardIsLoading,
		handleCloseDrawer,
		coordinates,
		cityName,
		isGeoFlow,
		districtNames,
	} = useSearchBarUnit({providers, type});

	return (
		<Filter.Root>
			<Stack direction={'row'} gap={1}>
				<Filter.Search
					data-tag-id={providerGuideTagging}
					placeholder={searchUnit}
					value={providerName}
					handleChange={value => setSearchByProviderName(type, value ?? '')}
					clearSearch={() => setSearchByProviderName(type, '')}
				/>

				<Fragment>
					<Filter.Button
						onClick={handleOpenDrawer}
						count={
							isDistanceFeatureEnabled && isHealthGuide
								? DISTANCE_MARKS[distanceIndex]?.label || ''
								: activeFiltersCount
						}
						customLabel={
							isDistanceFeatureEnabled && isHealthGuide ? distanceText : undefined
						}
					/>
					<Drawer.Root
						open={openDrawerState}
						onOpen={handleOpenDrawer}
						width="685px"
						customMaxHeight={'100%'}
						onClose={() => handleCloseDrawer()}
						sx={{zIndex: 9000}}>
						<Drawer.Header
							title={
								isDistanceFeatureEnabled && isHealthGuide ? filterDistance : filter
							}
							onClose={() => handleCloseDrawer()}
						/>
						<Fragment>
							{(!isDistanceFeatureEnabled || !isHealthGuide) && (
								<SortButtons value={localSortOrder} onChange={setLocalSortOrder} />
							)}
							<Drawer.Content gap={1} sx={{overflow: 'visible'}}>
								{isDistanceFeatureEnabled && isHealthGuide ? (
									<Fragment>
										<Typography
											variant="body1"
											fontWeight={600}
											color="text.secondary"
											mt={3}>
											{maxDistance}
										</Typography>

										{coordinates ? (
											<Typography variant="body2" color="text.secondary">
												{selectDistanceCoordinates}{' '}
												<Typography
													color="primary.dark"
													component="span"
													variant="body2"
													fontWeight={700}>
													{fromYourLocation}
												</Typography>
												.
											</Typography>
										) : (
											<Typography variant="body2" color="text.secondary">
												{selectDistance}{' '}
												<Typography
													color="primary.dark"
													component="span"
													variant="body2"
													fontWeight={700}>
													{formatText(cityName)}
												</Typography>
												.
											</Typography>
										)}

										<Box px={1}>
											<CustomSlider
												sx={{width: '100%'}}
												value={distanceSliderValue}
												step={null}
												size="small"
												valueLabelDisplay="off"
												marks={DISTANCE_MARKS}
												onChange={handleChangeDistance}
												$activeIndex={distanceIndex}
												max={3}
											/>
										</Box>
									</Fragment>
								) : (
									<Fragment>
										<Stack gap={0.5}>
											<Typography
												variant="body1"
												fontWeight={600}
												color="text.secondary">
												{specificNeighborhood}
											</Typography>

											<Typography variant="body2" color="text.secondary">
												{selectSpecificNeighborhood}
											</Typography>
										</Stack>

										{districtListIsLoading || healthCardIsLoading ? (
											<SkeletonInput label={loadingDistricts} />
										) : (
											<AutocompleteSelect
												disabled={!districtList?.length}
												customPlaceholder={selectDistrict}
												value={localSelectDistrict || districtNames}
												onValueChange={handleDistrictSelection}
												options={
													filterDistrictsByProviders(
														districtList || [],
													)?.map(district => ({
														bairro: district.bairro,
														cidade: district.cidade,
														uf: district.uf,
													})) ?? []
												}
												getOptionLabel={opt =>
													isGeoFlow
														? `${textHandle(opt?.bairro).capitalizeFirstLetter()} - ${textHandle(opt?.cidade).capitalizeFirstLetter()}`
														: textHandle(
																opt?.bairro,
															).capitalizeFirstLetter()
												}
												getOptionValue={opt =>
													formatDistrictValue(
														opt?.bairro,
														opt?.cidade,
														opt?.uf,
													) ?? ''
												}
												shouldDisablePortal
											/>
										)}
									</Fragment>
								)}
							</Drawer.Content>
							<Drawer.Footer
								firstButtonOnClick={handleClearFilter}
								secondButtonOnClick={handleApplyFilter}
							/>
						</Fragment>
					</Drawer.Root>
				</Fragment>
			</Stack>
			<CurrentLocation type={type} />
		</Filter.Root>
	);
}
